// Libs
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  ChartBarIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  ClockIcon as ClockIconOutline,
} from "@heroicons/react/24/outline";
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/solid";
import InformationCircleIcon from "@heroicons/react/24/outline/InformationCircleIcon";

// Local
import HeadingOne from "../../components/HeadingOne";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import SafeEmailPreview from "../../components/EmailMarketing/SafeEmailPreview";
import { StyledTooltip } from "../../components/StyledTooltip";
import {
  ExistingEmailCampaign,
  EmailCampaignStatus,
  EmailCampaignManager,
} from "../../models/email-campaign";
import { EmailCampaignStatistics } from "../../database/email-marketing";
import { EMAIL_CAMPAIGNS_URL } from "../../urls";

interface Props {
  siteKey: string;
  campaign: ExistingEmailCampaign;
  statistics: EmailCampaignStatistics | null;
  statisticsError?: string | null;
  loading?: boolean;
  error?: string | null;
}

export default function EmailCampaignDetailsPage({
  siteKey,
  campaign,
  statistics,
  statisticsError = null,
  loading = false,
  error = null,
}: Props) {
  const navigate = useNavigate();

  const handleBackToCampaigns = () => {
    navigate(EMAIL_CAMPAIGNS_URL);
  };

  const getStatusIcon = (status: EmailCampaignStatus) => {
    switch (status) {
      case EmailCampaignStatus.COMPLETED:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case EmailCampaignStatus.SENDING:
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      case EmailCampaignStatus.PENDING:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case EmailCampaignStatus.FAILED:
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case EmailCampaignStatus.CANCELLED:
        return <XCircleIcon className="h-5 w-5 text-gray-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: EmailCampaignStatus) => {
    switch (status) {
      case EmailCampaignStatus.COMPLETED:
        return "text-green-700 bg-green-50";
      case EmailCampaignStatus.SENDING:
        return "text-blue-700 bg-blue-50";
      case EmailCampaignStatus.PENDING:
        return "text-yellow-700 bg-yellow-50";
      case EmailCampaignStatus.FAILED:
        return "text-red-700 bg-red-50";
      case EmailCampaignStatus.CANCELLED:
        return "text-gray-700 bg-gray-50";
      default:
        return "text-gray-700 bg-gray-50";
    }
  };

  const formatPercentage = (value: number) => {
    if (!isFinite(value) || isNaN(value)) {
      return "0.0%";
    }
    return `${value.toFixed(1)}%`;
  };

  const calculatePercentage = (
    numerator: number,
    denominator: number,
  ): number => {
    if (denominator === 0 || !isFinite(denominator) || !isFinite(numerator)) {
      return 0;
    }
    return (numerator / denominator) * 100;
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "N/A";
    return timestamp.toDate().toLocaleString();
  };

  // Calculate sending progress for campaigns in progress
  const isInProgress = EmailCampaignManager.isInProgress(campaign);
  // Include failed count in progress calculation since failed emails are also "processed"
  const processedCount = campaign.sentCount + campaign.failedCount;
  const sendingProgress =
    isInProgress && campaign.totalRecipients > 0
      ? Math.round((processedCount / campaign.totalRecipients) * 100)
      : 0;

  const renderStatisticsError = () => (
    <div className="rounded-md border border-red-200 bg-red-50 p-4">
      <div className="flex">
        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">
            Statistics Unavailable
          </h3>
          <p className="mt-1 text-sm text-red-700">{statisticsError}</p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-md border border-red-200 bg-red-50 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error Loading Campaign
              </h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6 border-b border-gray-200 pb-6">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="mb-3 flex items-center space-x-3">
              <BaseButtonSecondary
                onClick={handleBackToCampaigns}
                className="flex items-center gap-2 text-sm"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back to Campaigns
              </BaseButtonSecondary>
            </div>
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <HeadingOne className="mb-2">
                  {campaign.campaignName}
                </HeadingOne>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(campaign.status)}
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(campaign.status)}`}
                    >
                      {campaign.status}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span>Template:</span>
                    <span className="font-medium text-gray-900">
                      {campaign.templateName}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span>Created:</span>
                    <span className="font-medium text-gray-900">
                      {formatDate(campaign.timestampCreated)}
                    </span>
                  </div>
                </div>
              </div>
              {statistics && (
                <div className="ml-6 flex space-x-6 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">
                      {statistics.sent}
                    </div>
                    <div className="text-xs text-gray-500">Sent</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">
                      {formatPercentage(
                        calculatePercentage(
                          statistics.uniqueOpens,
                          statistics.tracked,
                        ),
                      )}
                    </div>
                    <div className="text-xs text-gray-500">Open Rate</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-600">
                      {formatPercentage(
                        calculatePercentage(
                          statistics.uniqueClicked,
                          statistics.tracked,
                        ),
                      )}
                    </div>
                    <div className="text-xs text-gray-500">Click Rate</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Campaign Progress - Only show for campaigns in progress */}
      {isInProgress && (
        <div className="mb-6 rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center">
            <ClockIconOutline className="mr-2 h-6 w-6 text-blue-500" />
            <h2 className="text-lg font-medium text-gray-900">
              Campaign Progress
            </h2>
            <div className="ml-auto flex items-center space-x-2">
              <div className="h-2 w-2 animate-pulse rounded-full bg-blue-500"></div>
              <span className="text-sm text-blue-600">
                {campaign.status === EmailCampaignStatus.PENDING
                  ? "Queued"
                  : "Sending"}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                Sending Progress
              </span>
              <span className="text-sm font-medium text-gray-900">
                {processedCount} of {campaign.totalRecipients} processed (
                {sendingProgress}%)
                {campaign.failedCount > 0 && (
                  <span className="block text-xs text-gray-500">
                    ({campaign.sentCount} sent, {campaign.failedCount} failed)
                  </span>
                )}
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-gray-200">
              <div
                className="h-2 rounded-full bg-blue-600 transition-all duration-500 ease-out"
                style={{ width: `${sendingProgress}%` }}
              ></div>
            </div>
          </div>

          {/* Real-time metrics */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {campaign.sentCount}
              </div>
              <div className="text-xs text-gray-500">Successfully Sent</div>
            </div>
            <div className="text-center">
              <div
                className={`text-2xl font-bold ${campaign.failedCount > 0 ? "text-red-600" : "text-gray-900"}`}
              >
                {campaign.failedCount}
              </div>
              <div className="text-xs text-gray-500">Failed to Send</div>
            </div>
            <div className="text-center">
              <div
                className={`text-2xl font-bold ${campaign.bouncedCount > 0 ? "text-red-600" : "text-gray-900"}`}
              >
                {campaign.bouncedCount}
              </div>
              <div className="text-xs text-gray-500">Bounced</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {processedCount}
              </div>
              <div className="text-xs text-gray-500">Total Processed</div>
            </div>
          </div>

          {campaign.status === EmailCampaignStatus.PENDING && (
            <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-3">
              <div className="flex items-center">
                <ClockIconOutline className="h-5 w-5 text-blue-600" />
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Campaign queued:</strong> Emails are being prepared
                    for sending. You'll see sending progress update in real-time
                    once delivery begins.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Campaign Results - Show for completed campaigns */}
      {!isInProgress && campaign.totalRecipients > 0 && (
        <div className="mb-6 rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center">
            <ChartBarIcon className="mr-2 h-6 w-6 text-gray-400" />
            <h2 className="text-lg font-medium text-gray-900">
              Campaign Results
            </h2>
            <div className="ml-auto">
              <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                {getStatusIcon(campaign.status)}
                <span className="ml-1">{campaign.status}</span>
              </span>
            </div>
          </div>

          {/* Campaign Summary */}
          <div className="mb-4 rounded-lg bg-gray-50 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-900">
                  Total Campaign Results
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  {campaign.totalRecipients} recipients targeted •{" "}
                  {processedCount} attempts processed
                </p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-gray-900">
                  {Math.round(
                    (processedCount / campaign.totalRecipients) * 100,
                  )}
                  % processed
                </div>
                <div className="text-xs text-gray-500">
                  {campaign.totalRecipients - processedCount > 0 && (
                    <span className="text-orange-600">
                      {campaign.totalRecipients - processedCount} not attempted
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Detailed breakdown */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {campaign.sentCount}
              </div>
              <div className="text-xs text-gray-500">Successfully Sent</div>
              <div className="mt-1 text-xs text-gray-600">
                {campaign.totalRecipients > 0
                  ? Math.round(
                      (campaign.sentCount / campaign.totalRecipients) * 100,
                    )
                  : 0}
                % of total
              </div>
            </div>
            <div className="text-center">
              <div
                className={`text-2xl font-bold ${campaign.failedCount > 0 ? "text-red-600" : "text-gray-900"}`}
              >
                {campaign.failedCount}
              </div>
              <div className="text-xs text-gray-500">Failed to Send</div>
              <div
                className={`mt-1 text-xs ${campaign.failedCount > 0 ? "text-red-600" : "text-gray-600"}`}
              >
                {campaign.totalRecipients > 0
                  ? Math.round(
                      (campaign.failedCount / campaign.totalRecipients) * 100,
                    )
                  : 0}
                % of total
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {statistics ? statistics.sent : campaign.sentCount}
              </div>
              <div className="text-xs text-gray-500">
                Successfully Delivered
              </div>
              <div className="mt-1 text-xs text-gray-600">
                {campaign.sentCount > 0
                  ? Math.round(
                      ((statistics ? statistics.sent : campaign.sentCount) /
                        campaign.sentCount) *
                        100,
                    )
                  : 0}
                % of sent
              </div>
            </div>
            <div className="text-center">
              <div
                className={`text-2xl font-bold ${(statistics ? statistics.bounced : campaign.bouncedCount) > 0 ? "text-red-600" : "text-gray-900"}`}
              >
                {statistics ? statistics.bounced : campaign.bouncedCount}
              </div>
              <div className="text-xs text-gray-500">Bounced</div>
              <div
                className={`mt-1 text-xs ${(statistics ? statistics.bounced : campaign.bouncedCount) > 0 ? "text-red-600" : "text-gray-600"}`}
              >
                {campaign.sentCount > 0
                  ? Math.round(
                      ((statistics
                        ? statistics.bounced
                        : campaign.bouncedCount) /
                        campaign.sentCount) *
                        100,
                    )
                  : 0}
                % of sent
              </div>
            </div>
          </div>

          {/* Additional context for failed emails */}
          {campaign.failedCount > 0 && (
            <div className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 p-3">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    {campaign.failedCount} emails failed to send
                  </h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    Failed emails are typically due to suppressed recipients,
                    invalid email addresses, or delivery restrictions. This
                    count shows all delivery attempts that were rejected before
                    reaching the recipient.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Campaign Overview */}
      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Campaign Details */}
        <div className="rounded-lg bg-white p-6 shadow lg:col-span-2">
          <h2 className="mb-4 text-lg font-medium text-gray-900">
            Campaign Details
          </h2>
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Subject Line
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {campaign.renderedSubject}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Total Recipients
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {campaign.totalRecipients.toLocaleString()} recipients targeted
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Started</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatDate(campaign.timestampStarted)}
              </dd>
            </div>
            {campaign.timestampCompleted && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Completed</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(campaign.timestampCompleted)}
                </dd>
              </div>
            )}
            {campaign.description && (
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">
                  Description
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {campaign.description}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Performance Summary */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="mb-4 text-lg font-medium text-gray-900">
            Performance Summary
          </h2>
          {statistics ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center">
                <div className="text-xl font-bold text-gray-900">
                  {statistics.sent}
                </div>
                <div className="text-xs text-gray-500">Total Sent</div>
              </div>
              {campaign.failedCount > 0 && (
                <div className="text-center">
                  <div
                    className={`text-xl font-bold ${campaign.failedCount > 0 ? "text-red-600" : "text-gray-900"}`}
                  >
                    {campaign.failedCount}
                  </div>
                  <div className="text-xs text-gray-500">Failed</div>
                </div>
              )}
              <div className="text-center">
                <div
                  className={`text-xl font-bold ${statistics.bounced > 0 ? "text-red-600" : "text-gray-900"}`}
                >
                  {statistics.bounced}
                </div>
                <div className="text-xs text-gray-500">Bounced</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-gray-900">
                  {formatPercentage(
                    calculatePercentage(statistics.tracked, statistics.sent),
                  )}
                </div>
                <div className="text-xs text-gray-500">Delivery Rate</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-gray-900">
                  {formatPercentage(
                    calculatePercentage(
                      statistics.uniqueOpens,
                      statistics.tracked,
                    ),
                  )}
                </div>
                <div className="text-xs text-gray-500">Open Rate</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-gray-900">
                  {formatPercentage(
                    calculatePercentage(
                      statistics.uniqueClicked,
                      statistics.tracked,
                    ),
                  )}
                </div>
                <div className="text-xs text-gray-500">Click Rate</div>
              </div>
              <div className="text-center">
                <div
                  className={`text-xl font-bold ${statistics.spamComplaints > 0 ? "text-red-600" : "text-gray-900"}`}
                >
                  {formatPercentage(
                    calculatePercentage(
                      statistics.spamComplaints,
                      statistics.sent,
                    ),
                  )}
                </div>
                <div className="text-xs text-gray-500">Spam Rate</div>
              </div>
            </div>
          ) : statisticsError ? (
            renderStatisticsError()
          ) : (
            <div className="text-center text-gray-500">
              <p className="text-sm">No statistics available yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Detailed Statistics */}
      <div className="mb-6 rounded-lg bg-white p-6 shadow">
        <div className="mb-4 flex items-center">
          <ChartBarIcon className="mr-2 h-6 w-6 text-gray-400" />
          <h2 className="text-lg font-medium text-gray-900">
            Detailed Analytics
          </h2>
        </div>

        {statistics ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Delivery Metrics */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">Delivery</h3>
                <StyledTooltip title="Metrics related to email delivery and sending">
                  <InformationCircleIcon className="h-4 w-4 text-gray-600" />
                </StyledTooltip>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="flex items-center gap-1 text-xs text-gray-700">
                    Total Recipients
                    <StyledTooltip title="Total number of email addresses targeted for this campaign">
                      <InformationCircleIcon className="h-3 w-3 text-gray-500" />
                    </StyledTooltip>
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {campaign.totalRecipients}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="flex items-center gap-1 text-xs text-gray-700">
                    Sent
                    <StyledTooltip title="Number of emails successfully delivered to recipient inboxes">
                      <InformationCircleIcon className="h-3 w-3 text-gray-500" />
                    </StyledTooltip>
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {statistics.sent}
                  </span>
                </div>
                {campaign.failedCount > 0 && (
                  <div className="flex justify-between">
                    <span className="flex items-center gap-1 text-xs text-gray-700">
                      Failed
                      <StyledTooltip title="Emails that failed to send due to invalid addresses, suppression, or other pre-delivery issues">
                        <InformationCircleIcon className="h-3 w-3 text-gray-500" />
                      </StyledTooltip>
                    </span>
                    <span
                      className={`text-sm font-semibold ${campaign.failedCount > 0 ? "text-red-600" : "text-gray-900"}`}
                    >
                      {campaign.failedCount}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="flex items-center gap-1 text-xs text-gray-700">
                    Tracked
                    <StyledTooltip title="Number of delivered emails that have tracking enabled for opens and clicks">
                      <InformationCircleIcon className="h-3 w-3 text-gray-500" />
                    </StyledTooltip>
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {statistics.tracked}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="flex items-center gap-1 text-xs text-gray-700">
                    Bounced
                    <StyledTooltip title="Emails that were returned undelivered after being sent (hard bounces and soft bounces)">
                      <InformationCircleIcon className="h-3 w-3 text-gray-500" />
                    </StyledTooltip>
                  </span>
                  <span
                    className={`text-sm font-semibold ${statistics.bounced > 0 ? "text-red-600" : "text-gray-900"}`}
                  >
                    {statistics.bounced}
                  </span>
                </div>
              </div>
            </div>

            {/* Engagement Metrics */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">
                  Engagement
                </h3>
                <StyledTooltip title="Metrics showing how recipients interact with your emails">
                  <InformationCircleIcon className="h-4 w-4 text-gray-600" />
                </StyledTooltip>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="flex items-center gap-1 text-xs text-gray-700">
                    Opens
                    <StyledTooltip title="Total number of times the email was opened (includes multiple opens by same recipient)">
                      <InformationCircleIcon className="h-3 w-3 text-gray-500" />
                    </StyledTooltip>
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {statistics.opens}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="flex items-center gap-1 text-xs text-gray-700">
                    Unique Opens
                    <StyledTooltip title="Number of unique recipients who opened the email at least once">
                      <InformationCircleIcon className="h-3 w-3 text-gray-500" />
                    </StyledTooltip>
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {statistics.uniqueOpens}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="flex items-center gap-1 text-xs text-gray-700">
                    Clicks
                    <StyledTooltip title="Total number of clicks on links in the email (includes multiple clicks by same recipient)">
                      <InformationCircleIcon className="h-3 w-3 text-gray-500" />
                    </StyledTooltip>
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {statistics.clicked}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="flex items-center gap-1 text-xs text-gray-700">
                    Unique Clicks
                    <StyledTooltip title="Number of unique recipients who clicked at least one link in the email">
                      <InformationCircleIcon className="h-3 w-3 text-gray-500" />
                    </StyledTooltip>
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {statistics.uniqueClicked}
                  </span>
                </div>
              </div>
            </div>

            {/* Quality Metrics */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">Quality</h3>
                <StyledTooltip title="Metrics indicating email quality and deliverability issues">
                  <InformationCircleIcon className="h-4 w-4 text-gray-600" />
                </StyledTooltip>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="flex items-center gap-1 text-xs text-gray-700">
                    Spam Complaints
                    <StyledTooltip title="Number of recipients who marked the email as spam or junk">
                      <InformationCircleIcon className="h-3 w-3 text-gray-500" />
                    </StyledTooltip>
                  </span>
                  <span
                    className={`text-sm font-semibold ${statistics.spamComplaints > 0 ? "text-red-600" : "text-gray-900"}`}
                  >
                    {statistics.spamComplaints}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="flex items-center gap-1 text-xs text-gray-700">
                    Bounce Rate
                    <StyledTooltip title="Percentage of sent emails that bounced back as undeliverable">
                      <InformationCircleIcon className="h-3 w-3 text-gray-500" />
                    </StyledTooltip>
                  </span>
                  <span
                    className={`text-sm font-semibold ${statistics.bounced > 0 ? "text-red-600" : "text-gray-900"}`}
                  >
                    {formatPercentage(
                      calculatePercentage(statistics.bounced, statistics.sent),
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Tracking Metrics */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">Tracking</h3>
                <StyledTooltip title="Advanced tracking and analytics data collected from email interactions">
                  <InformationCircleIcon className="h-4 w-4 text-gray-600" />
                </StyledTooltip>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="flex items-center gap-1 text-xs text-gray-700">
                    Client Recorded
                    <StyledTooltip title="Number of emails where client information (device/email client) was recorded">
                      <InformationCircleIcon className="h-3 w-3 text-gray-500" />
                    </StyledTooltip>
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {statistics.withClientRecorded}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="flex items-center gap-1 text-xs text-gray-700">
                    Platform Recorded
                    <StyledTooltip title="Number of emails where platform information (OS/browser) was recorded">
                      <InformationCircleIcon className="h-3 w-3 text-gray-500" />
                    </StyledTooltip>
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {statistics.withPlatformRecorded}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="flex items-center gap-1 text-xs text-gray-700">
                    Read Time Recorded
                    <StyledTooltip title="Number of emails where reading time/engagement duration was tracked">
                      <InformationCircleIcon className="h-3 w-3 text-gray-500" />
                    </StyledTooltip>
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {statistics.withReadTimeRecorded}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : statisticsError ? (
          renderStatisticsError()
        ) : (
          <div className="text-center text-gray-500">
            <p className="text-sm">No detailed analytics available yet</p>
          </div>
        )}
      </div>

      {/* Email Preview */}
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4 flex items-center">
          <EnvelopeIcon className="mr-2 h-6 w-6 text-gray-400" />
          <h2 className="text-lg font-medium text-gray-900">Email Preview</h2>
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200">
          <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
            <div className="text-sm text-gray-500">Subject:</div>
            <div className="font-medium text-gray-900">
              {campaign.renderedSubject}
            </div>
          </div>

          <SafeEmailPreview
            htmlContent={campaign.renderedHtmlContent}
            className="border-0"
          />
        </div>
      </div>
    </div>
  );
}
